import React, { useState, useCallback } from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import { useTranslation } from 'react-i18next'
import { DefaultTFuncReturn } from 'i18next'
import {
  Box,
  InputLabel,
  FormHelperText,
  Tooltip,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material'
import {
  MyLocation as MyLocationIcon,
  Directions as DirectionsIcon
} from '@mui/icons-material'

import MuiCard from '../MuiCard'
import GoogleMap, { DEFAULT_GOOGLE_MAP_ID, DEFAULT_ZOOM } from '../Map'
import MapMarker from '../MapMarker'

type MapFieldProps = {
  mapId?: string
  label?: string | DefaultTFuncReturn
  helperText?: string | DefaultTFuncReturn
  error?: boolean
  disabled?: boolean
  value?: google.maps.LatLngLiteral
  onClick?: (geolocation: google.maps.LatLngLiteral) => void
}

const MapField: React.FC<MapFieldProps> = ({
  mapId = DEFAULT_GOOGLE_MAP_ID,
  label,
  helperText,
  value,
  onClick,
  error = true,
  disabled = false
}: MapFieldProps) => {
  const { t } = useTranslation()
  const map = useMap(mapId)
  const [loadingCurrentLocation, setLoadingCurrentLocation] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = !!anchorEl
  const geolocationClient = navigator?.geolocation

  const handleShowCurrentLocation = useCallback(() => {
    if (geolocationClient) {
      setLoadingCurrentLocation(true)
      geolocationClient.getCurrentPosition(position => {
        const lat = position?.coords?.latitude
        const lng = position?.coords?.longitude
        const latLng = {
          lat,
          lng
        }

        map?.setCenter?.(latLng)
        map?.setZoom(DEFAULT_ZOOM)

        onClick?.(latLng)

        setLoadingCurrentLocation(false)
      })
    }
  }, [geolocationClient, map, onClick])

  const handleOpenNavigationMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    },
    []
  )

  const handleCloseNavigationMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleNavigateToGoogleMaps = useCallback(() => {
    if (value) {
      const { lat, lng } = value
      window.open(
        `https://www.google.com/maps?q=${lat},${lng}`,
        '_blank',
        'noopener,noreferrer'
      )
      handleCloseNavigationMenu()
    }
  }, [value, handleCloseNavigationMenu])

  const handleNavigateToWaze = useCallback(() => {
    if (value) {
      const { lat, lng } = value
      window.open(
        `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
        '_blank',
        'noopener,noreferrer'
      )
      handleCloseNavigationMenu()
    }
  }, [value, handleCloseNavigationMenu])

  return (
    <>
      <MuiCard>
        <Box
          position='relative'
          p={2}
          height={400}
          border={error ? 1 : undefined}
          borderColor={error ? 'error.main' : undefined}
        >
          {label ? (
            <Box mb={2}>
              <InputLabel>{label}</InputLabel>
            </Box>
          ) : null}

          <Box
            position='absolute'
            left={25}
            bottom={25}
            zIndex={1}
            bgcolor='common.white'
            display='flex'
            flexDirection='column'
            gap={1}
          >
            <Tooltip title={t('showCurrentLocation')}>
              <span>
                <IconButton
                  onClick={disabled ? undefined : handleShowCurrentLocation}
                  disabled={loadingCurrentLocation || disabled}
                >
                  {loadingCurrentLocation ? (
                    <CircularProgress size={20} sx={{ color: 'grey.700' }} />
                  ) : (
                    <MyLocationIcon
                      fontSize='small'
                      sx={{ color: 'grey.700' }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {value ? (
              <>
                <Tooltip title={t('navigateToLocation')}>
                  <span>
                    <IconButton
                      onClick={handleOpenNavigationMenu}
                      disabled={disabled}
                    >
                      <DirectionsIcon
                        fontSize='small'
                        sx={{ color: 'grey.700' }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleCloseNavigationMenu}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                >
                  <MenuItem onClick={handleNavigateToGoogleMaps}>
                    {t('googleMaps')}
                  </MenuItem>
                  <MenuItem onClick={handleNavigateToWaze}>
                    {t('waze')}
                  </MenuItem>
                </Menu>
              </>
            ) : null}
          </Box>

          <Box width='100%' height='90%'>
            <GoogleMap
              id={mapId}
              defaultCenter={value}
              onClick={event => {
                const latLng = event?.detail?.latLng
                if (latLng) {
                  onClick?.(latLng)
                }
              }}
            >
              <>{value ? <MapMarker position={value} /> : null}</>
            </GoogleMap>
          </Box>
        </Box>
      </MuiCard>

      {helperText ? (
        <FormHelperText error={error} variant='outlined' margin={undefined}>
          {helperText}
        </FormHelperText>
      ) : null}
    </>
  )
}

export default MapField
